// Recovering from a chunk that no longer exists.
//
// Cloudflare Pages serves only the current deployment's assets from the live
// hostname, so the moment we deploy, the hashed chunks the open tab knows about
// are gone. Every app mounts HeroUI's ToastProvider through UIProvider, and that
// is dynamically imported — so a tab (or an HTTP-cached index.html) from the
// previous build asks for a chunk that 404s, the SPA `/* /index.html 200`
// rewrite answers with HTML, and the browser throws
// "Importing a module script failed." The service-worker reload handles this for
// most users, but iOS in-app browsers run no service worker at all.
//
// Vite dispatches a cancelable `vite:preloadError` for exactly this; nothing
// listened for it before. One reload picks up the fresh index.html.

const RELOAD_KEY = 'sc_chunk_reload_at'
const RELOAD_COOLDOWN_MS = 30_000

interface PreloadErrorEvent extends Event {
  payload?: unknown
}

/**
 * True when a reload is worth attempting: we have not already reloaded for this
 * reason in the last 30 seconds. Reading sessionStorage can throw in a locked
 * down webview — treat that as "no record of a recent reload" rather than
 * blocking recovery.
 */
export function shouldAttemptReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0)
    return !Number.isFinite(last) || Date.now() - last >= RELOAD_COOLDOWN_MS
  } catch {
    return true
  }
}

/** Records a reload attempt so the next one inside the cooldown is skipped. */
export function markReloadAttempt(): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // Private-mode webviews can refuse writes. Losing the guard is survivable;
    // failing to recover is not.
  }
}

export interface StaleChunkRecoveryOptions {
  /** Called just before the reload, e.g. to report the recovery to Sentry. */
  onRecover?: (error: unknown) => void
}

/**
 * Reloads once when a dynamic import fails because its chunk is gone.
 *
 * If it happens again inside the cooldown the reload did not fix it, so the
 * event is left alone and surfaces as a real error — the noise goes away, the
 * signal does not. Returns an uninstall function.
 */
export function installStaleChunkRecovery(options: StaleChunkRecoveryOptions = {}): () => void {
  const onPreloadError = (event: Event) => {
    if (!shouldAttemptReload()) return
    event.preventDefault()
    markReloadAttempt()
    options.onRecover?.((event as PreloadErrorEvent).payload)
    window.location.reload()
  }

  window.addEventListener('vite:preloadError', onPreloadError)
  return () => window.removeEventListener('vite:preloadError', onPreloadError)
}
